import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell as PieCell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GetDashboardSummary } from "../../../Service/_index";
import { onPolicyEvent } from "../../../Service/realtime";
import { friendlyError } from "../../Policy/OnlinePolicy/utils/friendlyError";
import {
  FONT,
  NAVY,
  PRIMARY_BUTTON_SX,
} from "../../Policy/OnlinePolicy/components/formStyles";
import {
  CARD_SX,
  Empty,
  KpiCard,
  LIGHT_NAVY_BG,
  PageSkeleton,
  Row,
  SectionCard,
} from "./parts";
import {
  AUTOMATION,
  DEFAULT_PRESET,
  PENDING_TILES,
  ROLE_LABEL,
  buildPresets,
  changeOf,
  dueIn,
  greeting,
  inr,
  listStatusLabel,
  num,
  rangeLabel,
  trendLabel,
} from "./dashboardUtils";

/**
 * The Dashboard (2026-10-22 redesign). One call (GET /dashboard/summary) brings everything the login's role may see;
 * the server decides the role and the sections, this page only lays out what arrives:
 *   header      greeting, role, the date range (chips + custom dates), Refresh, "Updated hh:mm", live dot
 *   KPIs        online policies, Policy List policies, premium, automation success - each against the period before
 *   charts      the trend (policies or premium, online and list), the automation donut, policies by insurer
 *   sections    needs attention, pending work, renewals, wallets, top users, the per-client table (super admin) ...
 * Live: a policy change from the socket refreshes quietly, at most every 30 seconds. Phones: one column, the date
 * chips scroll sideways, tables become lists.
 */

const LIVE_GAP_MS = 30 * 1000;
const ONLINE = "#1E3A5F"; // the header's navy
const LIST = "#7FA7D9"; // a light blue of the same family

export default function DashboardPage() {
  const navigate = useNavigate();
  const phone = useMediaQuery("(max-width:599.95px)");
  const presets = useMemo(() => buildPresets(), []);
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [custom, setCustom] = useState(() => {
    const p = presets.find((x) => x.key === DEFAULT_PRESET);
    return { from: p.from, to: p.to };
  });
  const range = useMemo(() => {
    if (preset === "custom") return custom;
    const p = presets.find((x) => x.key === preset) || presets[0];
    return { from: p.from, to: p.to };
  }, [preset, custom, presets]);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const lastLoad = useRef(0);
  const asked = useRef(0);

  const load = useCallback(
    async ({ fresh = false, quiet = false } = {}) => {
      const ticket = ++asked.current; // only the newest answer is shown (a slow old range must not win)
      if (quiet) setRefreshing(true);
      else setLoading(true);
      try {
        const s = await GetDashboardSummary({
          from: range.from,
          to: range.to,
          fresh,
        });
        if (ticket !== asked.current) return;
        setSummary(s);
        setError("");
        setUpdatedAt(new Date());
        lastLoad.current = Date.now();
      } catch (e) {
        if (ticket !== asked.current) return;
        setError(
          (e && e.message) || "Could not load the dashboard. Please try again.",
        );
      } finally {
        if (ticket === asked.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [range.from, range.to],
  );

  useEffect(() => {
    load();
  }, [load]);

  // live: a policy changed somewhere in this login's view -> refresh quietly, at most every 30 seconds
  useEffect(() => {
    let timer = null;
    const stop = onPolicyEvent(() => {
      if (timer) return;
      const wait = Math.max(0, LIVE_GAP_MS - (Date.now() - lastLoad.current));
      timer = setTimeout(() => {
        timer = null;
        load({ fresh: true, quiet: true });
      }, wait);
    });
    return () => {
      stop();
      if (timer) clearTimeout(timer);
    };
  }, [load]);

  const role = summary ? summary.role : null;
  const data = (summary && summary.data) || {};
  const has = (s) =>
    !!(summary && summary.sections && summary.sections.includes(s));
  // super admin: no Pending / Team cards, so Brisk sits beside Needs attention instead of alone at the bottom
  const briskBeside =
    has("brisk") &&
    !!data.brisk &&
    has("attention") &&
    !!data.attention &&
    !has("pending") &&
    !has("users");
  const name =
    (typeof sessionStorage !== "undefined" && sessionStorage.getItem("name")) ||
    "";

  return (
    <Box
      data-testid="dashboard-page"
      sx={{
        // phones: the app frame's 26px sides are cut to 8px so the cards get the screen's width
        width: "100%",
        pb: 3,
        boxSizing: "border-box",
        fontFamily: FONT,
      }}
    >
      <Header
        name={name}
        role={role}
        presets={presets}
        preset={preset}
        onPreset={setPreset}
        custom={custom}
        onCustom={(next) => {
          setCustom(next);
          setPreset("custom");
        }}
        range={range}
        refreshing={refreshing || (loading && !!summary)}
        updatedAt={updatedAt}
        onRefresh={() => load({ fresh: true, quiet: !!summary })}
      />

      {loading && !summary && <PageSkeleton />}

      {!loading && !summary && error && (
        <Box
          data-testid="dashboard-error"
          sx={{ ...CARD_SX, p: 3, textAlign: "center" }}
        >
          <ReportProblemRoundedIcon sx={{ color: "#F04438", fontSize: 36 }} />
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "16px",
              color: "#101828",
              mt: 1,
            }}
          >
            The dashboard did not load
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "13px",
              color: "#475467",
              mt: 0.5,
              mb: 2,
            }}
          >
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => load({ fresh: true })}
            startIcon={<RefreshRoundedIcon />}
            sx={PRIMARY_BUTTON_SX}
          >
            Retry
          </Button>
        </Box>
      )}

      {summary && error && (
        <Box
          data-testid="dashboard-stale"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: "10px",
            backgroundColor: "#FFFAEB",
            border: "1px solid #FEDF89",
            color: "#B54708",
            fontSize: "13px",
          }}
        >
          <ReportProblemRoundedIcon sx={{ fontSize: 18 }} />
          <Box sx={{ flex: 1 }}>
            Could not refresh - these are the figures from{" "}
            {updatedAt ? dayjs(updatedAt).format("h:mm a") : "before"}.
          </Box>
          <Button
            size="small"
            onClick={() => load({ fresh: true, quiet: true })}
            sx={{ textTransform: "none", fontWeight: 700, color: "#B54708" }}
          >
            Retry
          </Button>
        </Box>
      )}

      {summary && role === "none" && (
        <Empty>There is nothing to show for this login yet.</Empty>
      )}

      {summary && role !== "none" && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity .2s" }}>
          {has("kpis") && <Kpis data={data} has={has} navigate={navigate} />}
          {has("myWallet") && data.myWallet && (
            <MyWallet wallet={data.myWallet} navigate={navigate} />
          )}
          <Grid>
            {has("trend") && (
              <Cell lg={has("automation") ? 8 : 12}>
                <TrendCard
                  trend={data.trend || []}
                  unit={summary.range && summary.range.unit}
                  phone={phone}
                />
              </Cell>
            )}
            {has("automation") && data.automation && (
              <Cell lg={4}>
                <AutomationCard
                  automation={data.automation}
                  successRate={data.kpis && data.kpis.successRate}
                />
              </Cell>
            )}
            {has("attention") && data.attention && (
              <Cell lg={has("pending") ? 6 : briskBeside ? 8 : 12} md={12}>
                <AttentionCard attention={data.attention} navigate={navigate} />
              </Cell>
            )}
            {briskBeside && (
              <Cell lg={4} md={12}>
                <BriskCard brisk={data.brisk} />
              </Cell>
            )}
            {has("pending") && data.pending && (
              <Cell lg={has("attention") ? 6 : 12} md={12}>
                <PendingCard pending={data.pending} navigate={navigate} />
              </Cell>
            )}
            {has("insurers") && data.insurers && (
              <Cell lg={7}>
                <InsurerCard insurers={data.insurers} phone={phone} />
              </Cell>
            )}
            {has("kpis") && (
              <Cell lg={has("insurers") ? 5 : 12}>
                <ListStatusCard
                  statuses={(data.kpis && data.kpis.listStatus) || []}
                  navigate={navigate}
                />
              </Cell>
            )}
            {has("clients") && data.clients && (
              <Cell lg={12}>
                <ClientsCard clients={data.clients} phone={phone} />
              </Cell>
            )}
            {has("renewals") && data.renewals && (
              <Cell lg={6}>
                <RenewalsCard renewals={data.renewals} navigate={navigate} />
              </Cell>
            )}
            {has("topUsers") && data.topUsers && (
              <Cell lg={6}>
                <TopUsersCard users={data.topUsers} />
              </Cell>
            )}
            {has("wallets") && data.wallets && (
              <Cell lg={6}>
                <WalletsCard
                  wallets={data.wallets}
                  userWallets={has("userWallets") ? data.userWallets : null}
                  navigate={navigate}
                />
              </Cell>
            )}
            {has("brisk") && data.brisk && !briskBeside && (
              <Cell lg={3} md={6}>
                <BriskCard brisk={data.brisk} />
              </Cell>
            )}
            {has("users") && data.users && (
              <Cell lg={3} md={6}>
                <UsersCard users={data.users} navigate={navigate} />
              </Cell>
            )}
          </Grid>
          <Typography
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: "11.5px",
              color: "#98A2B3",
              fontFamily: FONT,
            }}
          >
            {rangeLabel(
              summary.range && summary.range.from,
              summary.range && summary.range.to,
            )}
            {typeof summary.tookMs === "number"
              ? ` · loaded in ${summary.tookMs} ms`
              : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ layout */

const Grid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
      gap: { xs: 1.5, sm: 2 },
      alignItems: "stretch",
    }}
  >
    {children}
  </Box>
);
const Cell = ({ lg = 12, md = 12, children }) => (
  <Box
    sx={{
      gridColumn: { xs: "span 12", md: `span ${md}`, lg: `span ${lg}` },
      minWidth: 0,
      display: "flex",
      "& > *": { flex: 1 },
    }}
  >
    {children}
  </Box>
);

/* ------------------------------------------------------------------ header */

function Header({
  name,
  role,
  presets,
  preset,
  onPreset,
  custom,
  onCustom,
  range,
  refreshing,
  updatedAt,
  onRefresh,
}) {
  const today = dayjs().format("YYYY-MM-DD");
  return (
    <Box
      sx={{
        borderRadius: "16px",
        mb: 2,
        color: NAVY,
        background: LIGHT_NAVY_BG,
        border: "1px solid #D5DFEC",
        px: { xs: 1.75, sm: 3 },
        pt: { xs: 1.75, sm: 2.5 },
        pb: { xs: 1.5, sm: 2 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: { xs: "18px", sm: "22px" },
              lineHeight: 1.25,
            }}
          >
            {greeting()}
            {name ? `, ${name}` : ""}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              flexWrap: "wrap",
            }}
          >
            {role && ROLE_LABEL[role] && (
              <Chip
                data-testid="dashboard-role"
                size="small"
                label={ROLE_LABEL[role]}
                sx={{
                  height: 22,
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: "11px",
                  color: "#fff",
                  backgroundColor: NAVY,
                }}
              />
            )}
            <Typography
              data-testid="dashboard-range"
              sx={{
                fontFamily: FONT,
                fontSize: "12.5px",
                color: "#5B6B82",
              }}
            >
              {rangeLabel(range.from, range.to)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          {updatedAt && (
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 0.75,
                fontSize: "12px",
                color: "#5B6B82",
                fontFamily: FONT,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#32D583",
                }}
              />
              Updated {dayjs(updatedAt).format("h:mm a")}
            </Box>
          )}
          <Tooltip title="Refresh" arrow>
            <span>
              <IconButton
                onClick={onRefresh}
                disabled={refreshing}
                aria-label="Refresh"
                data-testid="dashboard-refresh"
                sx={{
                  color: NAVY,
                  backgroundColor: "#fff",
                  border: "1px solid #C9D7E8",
                  borderRadius: "10px",
                  width: 36,
                  height: 36,
                  "&.Mui-disabled": { color: "#98A2B3" },
                }}
              >
                <RefreshRoundedIcon
                  fontSize="small"
                  sx={{
                    animation: refreshing
                      ? "dashSpin 0.9s linear infinite"
                      : "none",
                    "@keyframes dashSpin": {
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* the date range: chips (one sideways-scrolling row on a phone) and, for Custom, two date boxes */}
      <Box
        data-testid="dashboard-presets"
        sx={{
          display: "flex",
          gap: 0.75,
          mt: { xs: 1.5, sm: 2 },
          overflowX: "auto",
          flexWrap: { xs: "nowrap", md: "wrap" },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {[...presets, { key: "custom", label: "Custom" }].map((p) => {
          const on = preset === p.key;
          return (
            <Chip
              key={p.key}
              clickable
              onClick={() => onPreset(p.key)}
              label={p.label}
              aria-pressed={on}
              data-testid={`preset-${p.key}`}
              sx={{
                flexShrink: 0,
                height: 30,
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: "12.5px",
                color: on ? "#fff" : NAVY,
                backgroundColor: on ? NAVY : "#fff",
                border: `1px solid ${on ? NAVY : "#C9D7E8"}`,
                "&:hover": {
                  backgroundColor: on ? NAVY : "#EEF3F9",
                },
              }}
            />
          );
        })}
      </Box>
      {preset === "custom" && (
        <Box
          data-testid="dashboard-custom"
          sx={{ display: "flex", gap: 1, mt: 1.25, flexWrap: "wrap" }}
        >
          {[
            ["from", "From"],
            ["to", "To"],
          ].map(([key, label]) => (
            <TextField
              key={key}
              type="date"
              size="small"
              label={label}
              value={custom[key]}
              inputProps={{ max: today, "data-testid": `custom-${key}` }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const next = { ...custom, [key]: v };
                if (next.from > next.to)
                  next[key === "from" ? "to" : "from"] = v; // never a backwards range
                onCustom(next);
              }}
              sx={{
                flex: { xs: "1 1 140px", sm: "0 0 170px" },
                backgroundColor: "#fff",
                borderRadius: "8px",
                border: "1px solid #C9D7E8",
                "& .MuiInputLabel-root": {
                  color: NAVY,
                  fontWeight: 600,
                  backgroundColor: "#fff",
                  px: 0.5,
                  borderRadius: "4px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent !important",
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ KPIs */

function Kpis({ data, has, navigate }) {
  const k = data.kpis || {};
  const prev = k.previous || {};
  const premium = (k.onlinePremium || 0) + (k.listPremium || 0);
  const prevPremium = (prev.onlinePremium || 0) + (prev.listPremium || 0);
  const automation = has("automation") && data.automation;
  return (
    <Box
      data-testid="dashboard-kpis"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0,1fr))",
          md: "repeat(4, minmax(0,1fr))",
        },
        gap: { xs: 1.25, sm: 2 },
        mb: { xs: 1.5, sm: 2 },
      }}
    >
      <KpiCard
        testId="kpi-online"
        label="Online policies"
        value={num(k.onlineCount)}
        change={changeOf(k.onlineCount, prev.onlineCount)}
        hint={inr(k.onlinePremium, { short: true })}
        icon={<CloudDoneRoundedIcon fontSize="small" />}
        onClick={() => navigate("/onlinePolicy")}
      />
      <KpiCard
        testId="kpi-list"
        label="Policy list"
        value={num(k.listCount)}
        change={changeOf(k.listCount, prev.listCount)}
        hint={`${num(k.listApproved)} approved`}
        icon={<ReceiptLongRoundedIcon fontSize="small" />}
        onClick={() => navigate("/policyList")}
      />
      <KpiCard
        testId="kpi-premium"
        label="Total premium"
        value={inr(premium, { short: true })}
        change={changeOf(premium, prevPremium)}
        hint="online + list"
        icon={<CurrencyRupeeRoundedIcon fontSize="small" />}
      />
      {automation ? (
        <KpiCard
          testId="kpi-success"
          label="Automation success"
          value={
            k.successRate === null || k.successRate === undefined
              ? "—"
              : `${k.successRate}%`
          }
          hint={`${num(automation.completed)} done · ${num(automation.failed)} failed`}
          icon={<SmartToyRoundedIcon fontSize="small" />}
        />
      ) : (
        <KpiCard
          testId="kpi-approved"
          label="Approved policies"
          value={num(k.listApproved)}
          hint={
            k.listCount
              ? `${Math.round(((k.listApproved || 0) / k.listCount) * 100)}% of the list`
              : "of the list"
          }
          icon={<TaskAltRoundedIcon fontSize="small" />}
        />
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ the user's own wallet */

function MyWallet({ wallet, navigate }) {
  return (
    <Box
      data-testid="dashboard-my-wallet"
      sx={{
        ...CARD_SX,
        mb: { xs: 1.5, sm: 2 },
        p: { xs: 1.75, sm: 2.25 },
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        borderColor: wallet.low ? "#FECDCA" : "#EAECF0",
        backgroundColor: wallet.low ? "#FFFBFA" : "#fff",
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          backgroundColor: wallet.low ? "#F04438" : NAVY,
          flexShrink: 0,
        }}
      >
        <AccountBalanceWalletRoundedIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "12.5px",
            fontWeight: 600,
            color: "#475467",
          }}
        >
          My policy wallet
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "24px",
            fontWeight: 700,
            color: wallet.low ? "#B42318" : "#101828",
            lineHeight: 1.2,
          }}
        >
          {inr(wallet.balance)}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "12px",
            color: wallet.low ? "#B42318" : "#667085",
          }}
        >
          {wallet.low
            ? `Low - at or below the minimum of ${inr(wallet.minimum)}. Add money so policies are not held.`
            : wallet.minimum
              ? `Minimum ${inr(wallet.minimum)}`
              : "Used to pay for online policies"}
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={() => navigate("/userPolicyWallet")}
        sx={{ ...PRIMARY_BUTTON_SX, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
      >
        Open my wallet
      </Button>
    </Box>
  );
}

/* ------------------------------------------------------------------ charts */

const tooltipStyle = {
  borderRadius: 10,
  border: "1px solid #EAECF0",
  boxShadow: "0 6px 16px rgba(16,24,40,0.08)",
  fontFamily: "Inter, sans-serif",
  fontSize: 12,
};

/** The two series' colours, as a plain row above a chart. */
const ChartLegend = () => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      mb: 1,
      fontFamily: FONT,
      fontSize: "12px",
      color: "#475467",
    }}
  >
    {[
      ["Online", ONLINE],
      ["Policy list", LIST],
    ].map(([label, color]) => (
      <Box
        key={label}
        sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
      >
        <Box
          component="span"
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />
        {label}
      </Box>
    ))}
  </Box>
);

function TrendCard({ trend, unit, phone }) {
  const [mode, setMode] = useState("count");
  const rows = trend.map((t) => ({
    label: trendLabel(t.key, unit),
    Online: mode === "count" ? t.online : t.onlinePremium,
    List: mode === "count" ? t.list : t.listPremium,
  }));
  const total = rows.reduce((s, r) => s + (r.Online || 0) + (r.List || 0), 0);
  const fmt = (v) => (mode === "count" ? num(v) : inr(v, { short: true }));
  return (
    <SectionCard
      testId="dashboard-trend"
      title="Trend"
      subtitle={`${mode === "count" ? "Policies" : "Premium"} per ${unit === "month" ? "month" : "day"} - online and Policy List`}
      icon={<ShowChartRoundedIcon fontSize="small" />}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
          mt: -0.5,
          flexWrap: "wrap",
        }}
      >
        <ChartLegend />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(e, v) => v && setMode(v)}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: "12px",
              px: 1.5,
              py: 0.25,
            },
            "& .MuiToggleButton-root.Mui-selected": {
              color: "#fff",
              backgroundColor: NAVY,
              "&:hover": { backgroundColor: "#16304F" },
            },
          }}
        >
          <ToggleButton value="count">Policies</ToggleButton>
          <ToggleButton value="premium">Premium</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {total === 0 ? (
        <Empty>No policies in this period.</Empty>
      ) : (
        <Box sx={{ height: phone ? 220 : 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={rows}
              margin={{ top: 6, right: 22, left: phone ? -18 : -4, bottom: 0 }}
            >
              <defs>
                <linearGradient id="dashOnline" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ONLINE} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={ONLINE} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="dashList" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={LIST} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={LIST} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F2F4F7"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#667085" }}
                tickLine={false}
                axisLine={{ stroke: "#EAECF0" }}
                interval={Math.max(
                  0,
                  Math.ceil(rows.length / (phone ? 5 : 10)) - 1,
                )}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#667085" }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tickFormatter={fmt}
                width={mode === "count" ? 44 : 60}
              />
              <ChartTooltip
                contentStyle={tooltipStyle}
                formatter={(v, n) => [fmt(v), n]}
              />
              <Area
                isAnimationActive={false}
                type="monotone"
                dataKey="Online"
                stroke={ONLINE}
                strokeWidth={2}
                fill="url(#dashOnline)"
              />
              <Area
                isAnimationActive={false}
                type="monotone"
                dataKey="List"
                name="Policy list"
                stroke={LIST}
                strokeWidth={2}
                fill="url(#dashList)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </SectionCard>
  );
}

function AutomationCard({ automation, successRate }) {
  const slices = AUTOMATION.map((a) => ({
    ...a,
    value: automation[a.key] || 0,
  })).filter((a) => a.value > 0);
  const total = slices.reduce((s, a) => s + a.value, 0);
  return (
    <SectionCard
      testId="dashboard-automation"
      title="Automation"
      subtitle="How the online policies of this period ran"
      icon={<DonutLargeRoundedIcon fontSize="small" />}
    >
      {total === 0 ? (
        <Empty>No online policies in this period.</Empty>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", lg: "column" },
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: { xs: 140, sm: 170 },
              height: { xs: 140, sm: 170 },
              flexShrink: 0,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="label"
                  innerRadius="66%"
                  outerRadius="100%"
                  paddingAngle={slices.length > 1 ? 2 : 0}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {slices.map((s) => (
                    <PieCell key={s.key} fill={s.color} />
                  ))}
                </Pie>
                <ChartTooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: "22px",
                  color: "#101828",
                  lineHeight: 1,
                }}
              >
                {successRate === null || successRate === undefined
                  ? "—"
                  : `${successRate}%`}
              </Typography>
              <Typography
                sx={{ fontFamily: FONT, fontSize: "11px", color: "#667085" }}
              >
                success
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
            {AUTOMATION.map((a) => (
              <Box
                key={a.key}
                data-testid={`automation-${a.key}`}
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "3px",
                    backgroundColor: a.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: FONT,
                    fontSize: "12.5px",
                    color: "#344054",
                  }}
                >
                  {a.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#101828",
                  }}
                >
                  {num(automation[a.key])}
                </Typography>
              </Box>
            ))}
            {automation.waitingMoney > 0 && (
              <Typography
                sx={{
                  mt: 0.75,
                  fontFamily: FONT,
                  fontSize: "12px",
                  color: "#B54708",
                }}
              >
                {num(automation.waitingMoney)} waiting for wallet money
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </SectionCard>
  );
}

/** Online and Policy List policies per insurer, stacked (never added into one figure - they are different books). */
function InsurerCard({ insurers, phone }) {
  const merged = new Map();
  for (const [src, rows] of [
    ["Online", insurers.online || []],
    ["List", insurers.list || []],
  ]) {
    for (const r of rows) {
      const key = String(r.key || "Other");
      const m = merged.get(key) || {
        name: key,
        Online: 0,
        List: 0,
        premium: 0,
      };
      m[src] += r.count || 0;
      m.premium += r.premium || 0;
      merged.set(key, m);
    }
  }
  const rows = Array.from(merged.values())
    .sort((a, b) => b.Online + b.List - (a.Online + a.List))
    .slice(0, 8);
  return (
    <SectionCard
      testId="dashboard-insurers"
      title="By insurer"
      subtitle="Policies of this period - the top 8"
      icon={<ApartmentRoundedIcon fontSize="small" />}
    >
      {rows.length === 0 ? (
        <Empty>No policies in this period.</Empty>
      ) : (
        <>
          <ChartLegend />
          <Box
            sx={{ height: Math.max(160, rows.length * (phone ? 36 : 40) + 24) }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={rows}
                layout="vertical"
                margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
                barCategoryGap="28%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#F2F4F7"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "#667085" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={phone ? 96 : 140}
                  tick={{ fontSize: 11.5, fill: "#344054" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) =>
                    String(v).length > (phone ? 11 : 20)
                      ? `${String(v).slice(0, phone ? 10 : 19)}…`
                      : v
                  }
                />
                <ChartTooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [num(v), n]}
                  cursor={{ fill: "#F9FAFB" }}
                />
                <Bar
                  isAnimationActive={false}
                  dataKey="Online"
                  stackId="a"
                  fill={ONLINE}
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  isAnimationActive={false}
                  dataKey="List"
                  name="Policy list"
                  stackId="a"
                  fill={LIST}
                  radius={[0, 6, 6, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ lists */

const REASON = {
  failed: { label: "Failed", color: "#B42318", bg: "#FEF3F2" },
  hold: { label: "On hold", color: "#B54708", bg: "#FFFAEB" },
  waitingMoney: { label: "Waiting for money", color: "#175CD3", bg: "#EFF8FF" },
};

function AttentionCard({ attention, navigate }) {
  const items = attention.items || [];
  const parts = [
    attention.failed ? `${attention.failed} failed` : "",
    attention.hold ? `${attention.hold} on hold` : "",
    attention.waitingMoney ? `${attention.waitingMoney} waiting for money` : "",
  ].filter(Boolean);
  return (
    <SectionCard
      testId="dashboard-attention"
      title="Needs attention"
      subtitle={
        parts.length
          ? parts.join(" · ")
          : "Online policies that failed, are on hold or wait for money"
      }
      icon={<ReportProblemRoundedIcon fontSize="small" />}
      action="Online policies"
      onAction={() => navigate("/onlinePolicy")}
      bodyMaxHeight={340}
    >
      {items.length === 0 ? (
        <Empty>Nothing needs attention right now.</Empty>
      ) : (
        items.map((it) => {
          const r = REASON[it.reason] || REASON.failed;
          return (
            <Row
              key={`${it.id}-${it.reason}`}
              testId="attention-row"
              onClick={() =>
                navigate(`/onlinePolicy/view/history/${it.id}`, {
                  state: { name: it.name },
                })
              }
              left={[it.name, it.policyNo].filter(Boolean).join(" · ")}
              sub={[
                it.company,
                it.error ? friendlyError(it.error).title : "",
                it.at ? dayjs(it.at).format("D MMM, h:mm a") : "",
              ]
                .filter(Boolean)
                .join(" · ")}
              right={
                <Chip
                  size="small"
                  label={r.label}
                  sx={{
                    height: 22,
                    fontFamily: FONT,
                    fontWeight: 700,
                    fontSize: "11px",
                    color: r.color,
                    backgroundColor: r.bg,
                  }}
                />
              }
            />
          );
        })
      )}
    </SectionCard>
  );
}

function PendingCard({ pending, navigate }) {
  const total = PENDING_TILES.reduce((s, t) => s + (pending[t.key] || 0), 0);
  return (
    <SectionCard
      testId="dashboard-pending"
      title="Pending work"
      subtitle={`${num(total)} waiting now - tap one to open it`}
      icon={<PendingActionsRoundedIcon fontSize="small" />}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0,1fr))",
            sm: "repeat(3, minmax(0,1fr))",
          },
          gap: 1.25,
        }}
      >
        {PENDING_TILES.map((t) => {
          const n = pending[t.key] || 0;
          return (
            <Box
              key={t.key}
              role="button"
              data-testid={`pending-${t.key}`}
              onClick={() => navigate(t.to)}
              sx={{
                p: 1.5,
                borderRadius: "12px",
                cursor: "pointer",
                border: `1px solid ${n ? "#C9D7E8" : "#EAECF0"}`,
                backgroundColor: n ? "#EEF3F9" : "#FCFCFD",
                transition: "all .15s",
                "&:hover": {
                  borderColor: NAVY,
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "22px",
                  fontWeight: 700,
                  color: n ? NAVY : "#98A2B3",
                  lineHeight: 1.1,
                }}
              >
                {num(n)}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#475467",
                  mt: 0.25,
                }}
              >
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
}

function ListStatusCard({ statuses, navigate }) {
  const total = statuses.reduce((s, r) => s + (r.count || 0), 0);
  const rows = [...statuses].sort((a, b) => b.count - a.count);
  return (
    <SectionCard
      testId="dashboard-list-status"
      title="Policy list by status"
      subtitle="Policies issued in this period"
      icon={<VerifiedUserRoundedIcon fontSize="small" />}
      action="Policy list"
      onAction={() => navigate("/policyList")}
    >
      {total === 0 ? (
        <Empty>No Policy List policies in this period.</Empty>
      ) : (
        rows.map((r) => (
          <Box key={r.key} sx={{ py: 0.75 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontFamily: FONT,
                fontSize: "12.5px",
                mb: 0.5,
              }}
            >
              <Box sx={{ color: "#344054", fontWeight: 600 }}>
                {listStatusLabel(r.key)}
              </Box>
              <Box sx={{ color: "#101828", fontWeight: 700 }}>
                {num(r.count)}{" "}
                <Box
                  component="span"
                  sx={{ color: "#98A2B3", fontWeight: 500 }}
                >
                  · {Math.round((r.count / total) * 100)}%
                </Box>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(r.count / total) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "#F2F4F7",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  backgroundColor:
                    r.key === "approvedPolicy"
                      ? ONLINE
                      : r.key === "rejectedPolicy"
                        ? "#F04438"
                        : LIST,
                },
              }}
            />
          </Box>
        ))
      )}
    </SectionCard>
  );
}

function RenewalsCard({ renewals, navigate }) {
  const boxes = [
    ["Today", renewals.today, "#F04438"],
    ["Next 7 days", renewals.week, NAVY],
    ["Next 30 days", renewals.month, NAVY],
  ];
  return (
    <SectionCard
      testId="dashboard-renewals"
      title="Renewals due"
      subtitle="Approved policies ending soon"
      icon={<EventRepeatRoundedIcon fontSize="small" />}
      action="Renewals"
      onAction={() => navigate("/renewalPolicy")}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0,1fr))",
          gap: 1,
          mb: 1.5,
        }}
      >
        {boxes.map(([label, n, color]) => (
          <Box
            key={label}
            sx={{
              p: 1.25,
              borderRadius: "10px",
              backgroundColor: "#F9FAFB",
              border: "1px solid #F2F4F7",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "20px",
                fontWeight: 700,
                color: n ? color : "#98A2B3",
                lineHeight: 1.1,
              }}
            >
              {num(n)}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "11.5px",
                color: "#667085",
                fontWeight: 600,
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          maxHeight: 260,
          overflowY: "auto",
          // the rows reach 8px past the list (their hover background): room for that, and never a sideways scroll
          overflowX: "hidden",
          mx: -1,
          px: 1,
          overscrollBehavior: "contain",
        }}
      >
        {(renewals.next || []).length === 0 ? (
          <Empty>No renewals in the next 7 days.</Empty>
        ) : (
          renewals.next.map((r) => (
            <Row
              key={r.id}
              testId="renewal-row"
              left={r.customer || r.policyNo || "Policy"}
              sub={[r.vehicle, r.company, r.policyNo]
                .filter(Boolean)
                .join(" · ")}
              right={dueIn(r.endsOn)}
              tone={dueIn(r.endsOn) === "Today" ? "#B42318" : NAVY}
            />
          ))
        )}
      </Box>
    </SectionCard>
  );
}

function TopUsersCard({ users }) {
  const max = Math.max(1, ...users.map((u) => u.online + u.list));
  return (
    <SectionCard
      testId="dashboard-top-users"
      title="Top users"
      subtitle="Most policies in this period"
      icon={<EmojiEventsRoundedIcon fontSize="small" />}
      bodyMaxHeight={380}
    >
      {users.length === 0 ? (
        <Empty>No policies in this period.</Empty>
      ) : (
        users.map((u, i) => (
          <Box
            key={u.id}
            data-testid="top-user-row"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              py: 1,
              "& + &": { borderTop: "1px solid #F2F4F7" },
            }}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: FONT,
                fontSize: "12px",
                fontWeight: 700,
                color: i < 3 ? "#fff" : NAVY,
                backgroundColor: i < 3 ? NAVY : "#EEF3F9",
              }}
            >
              {i + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#101828",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {u.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#101828",
                    flexShrink: 0,
                  }}
                >
                  {inr(u.premium, { short: true })}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#F2F4F7",
                    overflow: "hidden",
                    display: "flex",
                  }}
                >
                  <Box
                    sx={{
                      width: `${(u.online / max) * 100}%`,
                      backgroundColor: ONLINE,
                    }}
                  />
                  <Box
                    sx={{
                      width: `${(u.list / max) * 100}%`,
                      backgroundColor: LIST,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: "11.5px",
                    color: "#667085",
                    flexShrink: 0,
                  }}
                >
                  {num(u.online)} online · {num(u.list)} list
                </Typography>
              </Box>
            </Box>
          </Box>
        ))
      )}
    </SectionCard>
  );
}

function WalletsCard({ wallets, userWallets, navigate }) {
  const list = [...(wallets.wallets || [])].sort(
    (a, b) => Number(b.low) - Number(a.low) || a.balance - b.balance,
  );
  return (
    <SectionCard
      testId="dashboard-wallets"
      title="Insurer wallets"
      subtitle={`${inr(wallets.total)} in total${wallets.low ? ` · ${wallets.low} low` : ""}`}
      icon={<AccountBalanceWalletRoundedIcon fontSize="small" />}
      action="Wallets"
      onAction={() => navigate("/clientCompanyWallet")}
    >
      <Box
        sx={{
          maxHeight: 280,
          overflowY: "auto",
          // the rows reach 8px past the list (their hover background): room for that, and never a sideways scroll
          overflowX: "hidden",
          mx: -1,
          px: 1,
          overscrollBehavior: "contain",
        }}
      >
        {list.length === 0 ? (
          <Empty>No insurer wallets yet.</Empty>
        ) : (
          list.map((w, i) => (
            <Row
              key={`${w.clientId}-${w.company}-${i}`}
              testId="wallet-row"
              left={w.company || "Insurer"}
              sub={w.minimum ? `Minimum ${inr(w.minimum)}` : ""}
              right={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {w.low && (
                    <Chip
                      size="small"
                      label="Low"
                      sx={{
                        height: 20,
                        fontSize: "10.5px",
                        fontWeight: 700,
                        color: "#B42318",
                        backgroundColor: "#FEF3F2",
                      }}
                    />
                  )}
                  {inr(w.balance)}
                </Box>
              }
              tone={w.low ? "#B42318" : undefined}
            />
          ))
        )}
      </Box>
      {userWallets && (
        <Box
          data-testid="dashboard-user-wallets"
          role="button"
          onClick={() => navigate("/adminPolicyWallet")}
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: "10px",
            backgroundColor: "#F9FAFB",
            border: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            "&:hover": { borderColor: "#C9D7E8" },
          }}
        >
          <GroupsRoundedIcon sx={{ color: NAVY }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "12.5px",
                fontWeight: 600,
                color: "#344054",
              }}
            >
              Users' policy wallets
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "11.5px",
                color: userWallets.low ? "#B42318" : "#667085",
              }}
            >
              {num(userWallets.users)} users
              {userWallets.low ? ` · ${userWallets.low} low` : ""}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "14px",
              fontWeight: 700,
              color: "#101828",
            }}
          >
            {inr(userWallets.total)}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
}

function ClientsCard({ clients, phone }) {
  const cols = [
    ["Client", (c) => c.name],
    ["Online", (c) => num(c.online)],
    ["Online premium", (c) => inr(c.onlinePremium, { short: true })],
    [
      "Success",
      (c) =>
        c.successRate === null || c.successRate === undefined
          ? "—"
          : `${c.successRate}%`,
    ],
    ["Policy list", (c) => num(c.list)],
    ["List premium", (c) => inr(c.listPremium, { short: true })],
    ["Wallets", (c) => inr(c.wallet, { short: true })],
    [
      "Last activity",
      (c) => (c.last ? dayjs(c.last).format("D MMM, h:mm a") : "—"),
    ],
  ];
  return (
    <SectionCard
      testId="dashboard-clients"
      title="Clients"
      subtitle={`${num(clients.length)} with activity or money - this period`}
      icon={<ApartmentRoundedIcon fontSize="small" />}
      bodyMaxHeight={phone ? 460 : 420}
    >
      {clients.length === 0 ? (
        <Empty>No client activity in this period.</Empty>
      ) : phone ? (
        clients.map((c) => (
          <Row
            key={c.id}
            testId="client-row"
            left={c.name}
            sub={`${num(c.online)} online${c.successRate !== null && c.successRate !== undefined ? ` (${c.successRate}% ok)` : ""} · ${num(c.list)} list${c.lowWallets ? ` · ${c.lowWallets} low wallet${c.lowWallets > 1 ? "s" : ""}` : ""}`}
            right={inr((c.onlinePremium || 0) + (c.listPremium || 0), {
              short: true,
            })}
          />
        ))
      ) : (
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: FONT,
            fontSize: "13px",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {cols.map(([h], i) => (
                <Box
                  component="th"
                  key={h}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#F9FAFB",
                    textAlign: i ? "right" : "left",
                    px: 1.25,
                    py: 1,
                    fontSize: "11.5px",
                    fontWeight: 700,
                    color: "#475467",
                    borderBottom: "1px solid #EAECF0",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {clients.map((c) => (
              <Box
                component="tr"
                key={c.id}
                data-testid="client-row"
                sx={{ "&:hover": { backgroundColor: "#F9FAFB" } }}
              >
                {cols.map(([h, get], i) => (
                  <Box
                    component="td"
                    key={h}
                    sx={{
                      textAlign: i ? "right" : "left",
                      px: 1.25,
                      py: 1,
                      borderBottom: "1px solid #F2F4F7",
                      whiteSpace: "nowrap",
                      fontWeight: i ? 500 : 600,
                      color:
                        h === "Success" &&
                        c.successRate !== null &&
                        c.successRate < 70
                          ? "#B42318"
                          : h === "Wallets" && c.lowWallets
                            ? "#B42318"
                            : "#101828",
                    }}
                  >
                    {get(c)}
                    {h === "Wallets" && c.lowWallets ? (
                      <Chip
                        size="small"
                        label={`${c.lowWallets} low`}
                        sx={{
                          ml: 0.75,
                          height: 18,
                          fontSize: "10.5px",
                          fontWeight: 700,
                          color: "#B42318",
                          backgroundColor: "#FEF3F2",
                        }}
                      />
                    ) : null}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </SectionCard>
  );
}

function BriskCard({ brisk }) {
  return (
    <SectionCard
      testId="dashboard-brisk"
      title="Brisk certificates"
      subtitle="Created in this period"
      icon={<VerifiedUserRoundedIcon fontSize="small" />}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: "28px",
          fontWeight: 700,
          color: "#101828",
          lineHeight: 1.2,
        }}
      >
        {num(brisk.count)}
      </Typography>
      <Typography
        sx={{ fontFamily: FONT, fontSize: "12.5px", color: "#667085" }}
      >
        {inr(brisk.premium)} premium (new certificates)
      </Typography>
      {brisk.v1 > 0 && (
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "11.5px",
            color: "#98A2B3",
            mt: 0.5,
          }}
        >
          {num(brisk.v1)} from the old Brisk page
        </Typography>
      )}
    </SectionCard>
  );
}

// only these two login types are made now (2026-10-22); the server counts only them
const USER_TYPE = { user: "Users", operator: "Operators" };

function UsersCard({ users, navigate }) {
  return (
    <SectionCard
      testId="dashboard-users"
      title="Team"
      subtitle={`${num(users.total)} logins by role`}
      icon={<GroupsRoundedIcon fontSize="small" />}
      action="Users"
      onAction={() => navigate("/user")}
    >
      {(users.byType || []).length === 0 ? (
        <Empty>No users yet.</Empty>
      ) : (
        [...users.byType]
          .sort((a, b) => b.count - a.count)
          .map((t) => (
            <Row
              key={t.key}
              left={
                USER_TYPE[t.key] ||
                USER_TYPE[String(t.key).toLowerCase()] ||
                t.key
              }
              right={num(t.count)}
            />
          ))
      )}
    </SectionCard>
  );
}
